import { useCallback, useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import RFCExecutionHistoryComponent from "../../../../../components/import-file/rfc-execution-history.component";
import { translate } from "../../../../../translate/translate";
import Loading from "../../../../../components/loading/loading";
import { useImportRfcSap } from "./useImportRfcSap";
import { RfcType } from "../../../../../types";
import { DialogRfcRun } from "../../../../../components/dialog/dialog-rfc-run.component";
import { useSapImportStatus } from "../../../../../hooks/useSapImportStatus";
import { useNotificationContext } from "../../../../../contexts/notification-context";

export default function ImportRfcSapPageComponent({
  rfcType,
}: {
  rfcType?: RfcType;
}) {
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const { refreshNotifications } = useNotificationContext();
  const { latestStatus, requestStatus } = useSapImportStatus(rfcType);

  const handleRefresh = useCallback(() => {
    setRefreshTrigger((currentValue) => !currentValue);
  }, []);

  const handleTaskCreated = useCallback((taskId?: string) => {
    requestStatus(taskId);
  }, [requestStatus]);

  const { isLoading, showRrcFunctionName, handleExecuteImportStockPo } = useImportRfcSap(
    rfcType,
    handleRefresh,
    handleTaskCreated,
  );

  const [openImportModal, setOpenImportModal] = useState(false);

  const handleRunImport = () => {
    if (rfcType === RfcType.STOCK_AND_PO) {
      handleExecuteImportStockPo();
    } else {
      setOpenImportModal(true);
    }
  };

  useEffect(() => {
    if (!latestStatus) {
      return;
    }

    handleRefresh();
    refreshNotifications("sap-import-status");

  }, [handleRefresh, latestStatus, refreshNotifications]);

  return (
    <Box sx={{ pb: 2 }}>
      <Loading open={isLoading} />

      <Box sx={{ backgroundColor: "white", border: "1px solid #E0E0E0" }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <PlayCircleFilledIcon sx={{ color: "#546E7A", fontSize: 22 }} />
            <Typography variant="h6" fontWeight={700}>
              {translate.t("sap_import")}
            </Typography>
            <Typography>
              ({showRrcFunctionName()})
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<PlayCircleFilledIcon sx={{ fontSize: 20 }} />}
            onClick={handleRunImport}
            sx={{ borderRadius: "4px", height: "40px" }}
          >
            {translate.t("run_import")}
          </Button>
        </Box>

        <RFCExecutionHistoryComponent
          key={refreshTrigger.toString()}
          displayedColumnsShow={["select", "import_date", "status", "operation"]}
          displayedColumns={[
            "select",
            "import_date",
            "status_message",
            "operation",
            "action",
          ]}
          onlyInfoAction={true}
          rfcType={rfcType}
        />
      </Box>

      <DialogRfcRun
        open={openImportModal}
        onClose={() => setOpenImportModal(false)}
        rfcType={rfcType}
        onRefresh={handleRefresh}
        onTaskCreated={handleTaskCreated}
      />
    </Box>
  );
}
